import React, { useEffect, useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Autocomplete, Box, Chip, CircularProgress, Divider, Grid, Paper,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import useToken from '../../hooks/useToken';
import { TM_COLOR, NO_TM_COLOR, SEVERITY_STYLES } from './constants';
import { FindingFields } from '../shared/FindingDetails';

// Fixed lane order — position (not color) carries event-type identity, so the
// chart only ever needs one accent hue for its dots.
const TM_EVENT_LANES = [
  { key: 'TM_VIEWER_OPEN', label: 'Viewer Opened' },
  { key: 'DFD_ELEMENT_CLICK', label: 'Element Click' },
  { key: 'DFD_THREAT_CLICK', label: 'Threat Click' },
  { key: 'DFD_DESELECTED', label: 'Deselected' },
];
const TM_EVENT_LABELS = Object.fromEntries(TM_EVENT_LANES.map(l => [l.key, l.label]));
const TM_LANE_ORDER = TM_EVENT_LANES.map(l => l.label);

function TmInteractionDot({ cx, cy }) {
  if (cx === undefined || cy === undefined) return null;
  return <circle cx={cx} cy={cy} r={5} fill={TM_COLOR} stroke="#fff" strokeWidth={2} />;
}

function TmInteractionTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <Paper variant="outlined" sx={{ p: 1, fontSize: 13 }}>
      <Typography variant="body2" fontWeight={600}>{p.lane}</Typography>
      {p.element && <Typography variant="caption" display="block">Element: {p.element}</Typography>}
      {p.component && <Typography variant="caption" display="block">Component: {p.component}</Typography>}
      <Typography variant="caption" display="block" color="text.secondary">
        {p.elapsedLabel} · {new Date(p.timestamp).toLocaleTimeString()}
      </Typography>
    </Paper>
  );
}

function TmInteractionTimeline({ events }) {
  if (!events || events.length === 0) {
    return <Typography color="text.secondary">No threat model interactions recorded.</Typography>;
  }

  const hasElapsed = events.some(e => e.elapsed_seconds !== null && e.elapsed_seconds !== undefined);
  const points = events.map(e => ({
    x: hasElapsed ? +(e.elapsed_seconds / 60).toFixed(2) : null,
    lane: TM_EVENT_LABELS[e.event_type] || e.event_type,
    element: e.element,
    component: e.component,
    timestamp: e.timestamp,
    elapsedLabel: hasElapsed ? `${(e.elapsed_seconds / 60).toFixed(1)} min into session` : 'elapsed time unavailable',
  }));

  return (
    <Stack spacing={2}>
      {hasElapsed ? (
        <ResponsiveContainer width="100%" height={220}>
          <ScatterChart margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid stroke="#e0e0e0" />
            <XAxis
              type="number"
              dataKey="x"
              name="Minutes into session"
              label={{ value: 'Minutes into session', position: 'insideBottom', offset: -4, fontSize: 12 }}
              tick={{ fontSize: 11 }}
            />
            <YAxis
              type="category"
              dataKey="lane"
              name="Event"
              domain={TM_LANE_ORDER}
              allowDuplicatedCategory={false}
              tick={{ fontSize: 11 }}
              width={100}
            />
            <RechartsTooltip content={<TmInteractionTooltip />} cursor={{ strokeDasharray: '3 3' }} />
            <Scatter data={points} shape={<TmInteractionDot />} />
          </ScatterChart>
        </ResponsiveContainer>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Session start time unavailable — showing raw event order below instead of a timeline.
        </Typography>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Event</TableCell>
              <TableCell>Element</TableCell>
              <TableCell>Component</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((e, i) => (
              <TableRow key={i} hover>
                <TableCell>
                  {e.elapsed_seconds !== null && e.elapsed_seconds !== undefined
                    ? `${(e.elapsed_seconds / 60).toFixed(1)} min`
                    : new Date(e.timestamp).toLocaleTimeString()}
                </TableCell>
                <TableCell>{TM_EVENT_LABELS[e.event_type] || e.event_type}</TableCell>
                <TableCell>{e.element ?? '—'}</TableCell>
                <TableCell>{e.component ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function SectionHeader({ children }) {
  return (
    <Box sx={{ mt: 4, mb: 1.5 }}>
      <Typography variant="h6" fontWeight="bold">{children}</Typography>
      <Divider />
    </Box>
  );
}

function InfoRow({ label, value }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2">{value ?? '—'}</Typography>
    </Grid>
  );
}

function AnswerDisplay({ question }) {
  const { answer, question_type: type } = question;
  if (answer === null || answer === undefined || answer === '') {
    return <Chip size="small" label="Not answered" variant="outlined" />;
  }
  if (type === 'MCQ') {
    return <Chip size="small" color="primary" variant="outlined" label={answer} />;
  }
  if (type === 'CHECKBOX') {
    return (
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {answer.split(',').map(v => v.trim()).filter(Boolean).map(v => (
          <Chip key={v} size="small" color="primary" variant="outlined" label={v} />
        ))}
      </Stack>
    );
  }
  if (type === 'RANKING') {
    const items = answer.split(',').map(v => v.trim()).filter(Boolean);
    return (
      <Box component="ol" sx={{ m: 0, pl: 3 }}>
        {items.map((v, i) => <li key={i}><Typography variant="body2">{v}</Typography></li>)}
      </Box>
    );
  }
  return <Typography variant="body2" fontStyle="italic">&ldquo;{answer}&rdquo;</Typography>;
}

function QuestionnaireSection({ title, questions }) {
  if (!questions || questions.length === 0) {
    return (
      <>
        <SectionHeader>{title}</SectionHeader>
        <Typography color="text.secondary">No questions found for this participant's group.</Typography>
      </>
    );
  }
  return (
    <>
      <SectionHeader>{title}</SectionHeader>
      <Stack spacing={2}>
        {questions.map(q => (
          <Box key={q.question_id}>
            <Typography variant="body2" fontWeight={500}>{q.question_text}</Typography>
            <Box mt={0.5}><AnswerDisplay question={q} /></Box>
          </Box>
        ))}
      </Stack>
    </>
  );
}

function FindingHistory({ findingId, latestVersion }) {
  const { getValidToken } = useToken();
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggle = async () => {
    if (!open && versions === null) {
      setLoading(true);
      setError(null);
      try {
        const token = await getValidToken();
        const res = await fetch(`/api/admin/findings/${findingId}/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setVersions(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    setOpen(o => !o);
  };

  return (
    <Box>
      <Chip
        size="small"
        variant="outlined"
        clickable
        onClick={toggle}
        label={open ? 'Hide edit history' : `View edit history${latestVersion > 1 ? ` (${latestVersion} versions)` : ''}`}
      />
      {open && (
        <Box mt={1.5}>
          {loading && <Typography variant="body2" color="text.secondary">Loading history…</Typography>}
          {error && <Typography variant="body2" color="error">Failed to load history: {error}</Typography>}
          {versions && (
            <Stack spacing={1.5}>
              {versions.map(v => (
                <Paper key={v.version} variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                  <Typography sx={{ fontWeight: 600 }}>{v.name || 'Untitled finding'}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1} mt={0.5}>
                    <Typography variant="subtitle2">
                      Version {v.version}{v.version === latestVersion ? ' (current)' : ''}
                    </Typography>
                    {v.severity && (
                      <Chip
                        size="small"
                        label={v.severity}
                        sx={{ bgcolor: SEVERITY_STYLES[v.severity]?.bg, color: SEVERITY_STYLES[v.severity]?.text, fontWeight: 'bold' }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {new Date(v.created_at).toLocaleString(undefined, { timeZoneName: 'shortOffset' })}
                    </Typography>
                  </Stack>
                  <FindingFields finding={v} />
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}

function FindingAccordion({ finding }) {
  const severityStyle = finding.severity && SEVERITY_STYLES[finding.severity];
  const title = finding.name
    || (finding.vulnerability_type || []).join(', ')
    || 'Untitled finding';

  return (
    <Accordion sx={finding.is_deleted ? { opacity: 0.6 } : undefined}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ width: '100%', pr: 1 }}>
          <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>{title}</Typography>
          {finding.severity && (
            <Chip
              size="small"
              label={finding.severity}
              sx={{ bgcolor: severityStyle?.bg, color: severityStyle?.text, fontWeight: 'bold' }}
            />
          )}
          {finding.website_name && <Chip size="small" variant="outlined" label={finding.website_name} />}
          {finding.is_deleted && <Chip size="small" color="default" label="Deleted" />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <FindingFields finding={finding} />

          <Typography variant="caption" color="text.secondary">
            Submitted {new Date(finding.created_at).toLocaleString(undefined, { timeZoneName: 'shortOffset' })}
            {finding.updated_at !== finding.created_at && ` · Updated ${new Date(finding.updated_at).toLocaleString(undefined, { timeZoneName: 'shortOffset' })}`}
          </Typography>

          <FindingHistory findingId={finding.id} latestVersion={finding.latest_version} />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function ParticipantDossier() {
  const { getValidToken } = useToken();
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadAccounts() {
      try {
        const token = await getValidToken();
        const res = await fetch('/api/admin/accounts', { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const all = await res.json();
        setAccounts(all.filter(a => a.role === 'PLAYER'));
      } catch (e) {
        setError(e.message);
      }
    }
    loadAccounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected) {
      setDossier(null);
      return;
    }
    async function loadDossier() {
      setLoading(true);
      setError(null);
      try {
        const token = await getValidToken();
        const res = await fetch(`/api/admin/participant-dossier/${selected.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setDossier(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    loadDossier();
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Participant Dossier
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Select a participant to see their full profile: info, questionnaire answers, and submitted findings.
      </Typography>

      <Autocomplete
        options={accounts}
        value={selected}
        onChange={(_e, value) => setSelected(value)}
        getOptionLabel={(a) => a.full_name ? `${a.full_name} (${a.email})` : a.email}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderOption={(props, a) => (
          <li {...props} key={a.id}>
            <Stack direction="row" spacing={1} alignItems="center">
              <span>{a.full_name || a.email}</span>
              <Chip size="small" label={a.threat_model_provided ? 'TM' : 'No-TM'} sx={{ bgcolor: a.threat_model_provided ? TM_COLOR : NO_TM_COLOR, color: '#fff', fontSize: 11 }} />
            </Stack>
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Participant" placeholder="Search by name or email" />}
        sx={{ maxWidth: 480 }}
      />

      {error && <Typography color="error" mt={2}>Failed to load: {error}</Typography>}
      {loading && (
        <Box mt={3} display="flex" alignItems="center" gap={2}>
          <CircularProgress size={20} /><Typography>Loading participant data…</Typography>
        </Box>
      )}

      {dossier && !loading && (
        <>
          <SectionHeader>Participant Info</SectionHeader>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <InfoRow label="Full name" value={dossier.account.full_name} />
              <InfoRow label="Email" value={dossier.account.email} />
              <InfoRow label="Affiliation" value={dossier.account.affiliation} />
              <InfoRow label="Progress step" value={dossier.account.progress_step?.replace(/_/g, ' ')} />
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary" display="block">Group</Typography>
                <Chip
                  size="small"
                  label={dossier.account.tm_group ? 'TM' : 'No-TM'}
                  sx={{ bgcolor: dossier.account.tm_group ? TM_COLOR : NO_TM_COLOR, color: '#fff' }}
                />
              </Grid>
              <InfoRow label="Scheduled start" value={dossier.account.scheduled_start_at ? new Date(dossier.account.scheduled_start_at).toLocaleString() : null} />
              <InfoRow label="Account created" value={new Date(dossier.account.created_at).toLocaleString()} />
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary" display="block">Active</Typography>
                <Chip size="small" label={dossier.account.is_active ? 'Active' : 'Inactive'} color={dossier.account.is_active ? 'success' : 'default'} variant="outlined" />
              </Grid>
            </Grid>

            {dossier.assignments.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Assigned websites</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Website</TableCell>
                        <TableCell align="right">Order</TableCell>
                        <TableCell align="right">Time limit (min)</TableCell>
                        <TableCell>Started</TableCell>
                        <TableCell>Stopped</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dossier.assignments.map(a => (
                        <TableRow key={a.website_id}>
                          <TableCell>{a.website_name}</TableCell>
                          <TableCell align="right">{a.order_index}</TableCell>
                          <TableCell align="right">{a.time_limit_minutes ?? '—'}</TableCell>
                          <TableCell>{a.server_started_at ? new Date(a.server_started_at).toLocaleString() : '—'}</TableCell>
                          <TableCell>{a.server_stopped_at ? new Date(a.server_stopped_at).toLocaleString() : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>

          <QuestionnaireSection title="Pre-Experiment Questionnaire" questions={dossier.questionnaires.PRE} />
          <QuestionnaireSection title="Post-Experiment Questionnaire" questions={dossier.questionnaires.POST} />

          <SectionHeader>Threat Model Interactions</SectionHeader>
          {dossier.account.tm_group ? (
            <TmInteractionTimeline events={dossier.tm_interactions} />
          ) : (
            <Typography color="text.secondary">
              This participant was in the No-TM group — threat model interaction data is not applicable.
            </Typography>
          )}

          <SectionHeader>Findings ({dossier.findings.length})</SectionHeader>
          {dossier.findings.length === 0 ? (
            <Typography color="text.secondary">No findings submitted.</Typography>
          ) : (
            <Stack spacing={1}>
              {dossier.findings.map(f => <FindingAccordion key={f.id} finding={f} />)}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}
